// src/pages/Users/Inscription/RenewalModal.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  updateAdherent,
  addAdherentGroups,
} from "./../../../services/adherentServices";
import { getActiveGroupsList } from "./../../../services/groupServices";
import { sendNotificationToAdmin } from "./../../../services/notificationServices";
import {
  getDossierInscription,
  updateDossierInscription,
} from "./../../../services/dossierServices";
import { useUser } from "./../../Home/UserContext";
import Button from "../../../components/Button/Button";
import {
  FaCalendarAlt,
  FaCheck,
  FaChevronRight,
  FaSave,
  FaSearch,
  FaTimes,
  FaUser,
  FaUsers,
} from "react-icons/fa";
import { toast } from "react-toastify";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const toAbsolute = (u) => {
  if (!u) return "";

  const s = String(u).trim();

  if (
    /^https?:\/\//i.test(s) ||
    s.startsWith("data:") ||
    s.startsWith("blob:")
  ) {
    return s;
  }

  if (!s.startsWith("/uploads")) return s;

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${s}`;
};

const getBirthYear = (adherent) => {
  const value =
    adherent?.date_naissance ||
    adherent?.dateNaissance ||
    adherent?.birth_date ||
    adherent?.birthDate;

  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    const match = String(value).match(/\d{4}/);
    return match ? match[0] : "";
  }

  return String(date.getFullYear());
};

const getSeasonStartYear = (nextSeason) => {
  const match = String(nextSeason || "").match(/\d{4}/);
  return match ? Number(match[0]) : new Date().getFullYear();
};

const getAgeForSeason = (birthYear, nextSeason) => {
  const year = Number(birthYear);
  const seasonStartYear = getSeasonStartYear(nextSeason);

  if (!Number.isInteger(year) || !Number.isInteger(seasonStartYear)) {
    return null;
  }

  return seasonStartYear - year;
};

const getGroupSearchText = (group) =>
  normalize(`${group?.nom || ""} ${group?.annees_naissance || ""}`);

const groupMatchesBirthYear = (group, birthYear) => {
  if (!birthYear) return false;

  const targetYear = Number(birthYear);
  const text = String(group?.annees_naissance || "");

  if (!text) return false;

  const years = text.match(/\d{4}/g)?.map(Number) || [];

  return years.includes(targetYear);
};

const groupMatchesAgePlus = (group, birthYear, nextSeason) => {
  const age = getAgeForSeason(birthYear, nextSeason);

  if (age === null) return false;

  const text = getGroupSearchText(group);

  if (!text) return false;

  const adultWords = [
    "adulte",
    "adultes",
    "senior",
    "seniors",
    "veteran",
    "veterans",
    "loisir adulte",
    "loisirs adultes",
  ];

  if (adultWords.some((word) => text.includes(word)) && age >= 18) {
    return true;
  }

  const patterns = [
    /\+\s*(\d{1,2})\s*ans?/i,
    /(\d{1,2})\s*ans?\s*(et\s*)?\+/i,
    /a\s*partir\s*de\s*(\d{1,2})\s*ans?/i,
    /des\s*(\d{1,2})\s*ans?/i,
    /dès\s*(\d{1,2})\s*ans?/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (!match) continue;

    const minAge = Number(match[1]);

    if (Number.isInteger(minAge) && age >= minAge) {
      return true;
    }
  }

  return false;
};

const groupIsRecommended = (group, birthYear, nextSeason) => {
  return (
    groupMatchesBirthYear(group, birthYear) ||
    groupMatchesAgePlus(group, birthYear, nextSeason)
  );
};

const getGroupsFromAdherent = (adherent) => {
  const groups =
    adherent?.groups ||
    adherent?.Groups ||
    adherent?.groupes ||
    adherent?.Groupes ||
    [];

  return Array.isArray(groups) ? groups : [];
};

const getConnectedUserLabel = (user) => {
  const localUserInfo = safeJsonParse(localStorage.getItem("userInfo"), {});
  const source = user || localUserInfo || {};

  const prenom = source?.prenom || source?.firstName || "";
  const nom = source?.nom || source?.lastName || "";

  const fullName = `${prenom} ${nom}`.trim();

  return fullName || "Un utilisateur";
};

const getAuthToken = (user) => {
  return (
    user?.token ||
    localStorage.getItem("token") ||
    localStorage.getItem("userToken") ||
    ""
  );
};

/**
 * Payload envoyé lors d'un renouvellement.
 *
 * On ne touche pas à :
 * - cm_url
 * - date_cm
 * - validate_cm
 * - lien_drive
 *
 * On remet à zéro :
 * - formulaire_licence_a_signer
 * - formulaire_licence_signe
 * - licence_url
 * - validate_licence
 * - validate_reglement
 */
const DOSSIER_RENEWAL_RESET_PAYLOAD = {
  formulaire_licence_a_signer: null,
  formulaire_licence_signe: null,
  licence_url: null,
  validate_licence: false,
  validate_reglement: false,
};

function GroupChoiceCard({
  group,
  selected,
  recommended,
  alreadyLinked,
  onToggle,
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(group.id)}
      aria-pressed={selected}
      className={cn(
        "group relative w-full overflow-hidden rounded-[1.4rem] border p-4 text-left transition",
        "focus:outline-none focus:ring-2 focus:ring-blue-500/50",
        selected
          ? "border-blue-500 bg-blue-50 shadow-sm dark:border-blue-400 dark:bg-blue-500/10"
          : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-blue-300 hover:bg-slate-50 hover:shadow-md dark:border-white/10 dark:bg-slate-950/70 dark:hover:border-blue-400/60 dark:hover:bg-slate-900"
      )}
    >
      <div
        className={cn(
          "absolute inset-y-0 left-0 w-1 transition",
          selected
            ? "bg-blue-600"
            : recommended
            ? "bg-emerald-500"
            : "bg-transparent"
        )}
      />

      <div className="flex items-start gap-3">
        <span
          className={cn(
            "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition",
            selected
              ? "border-blue-600 bg-blue-600 text-white"
              : "border-slate-300 bg-white text-transparent dark:border-slate-600 dark:bg-slate-900"
          )}
        >
          <FaCheck className="text-xs" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-slate-900 dark:text-white">
              {group?.nom || "Groupe sans nom"}
            </span>

            {recommended && (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">
                conseillé
              </span>
            )}

            {alreadyLinked && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">
                déjà lié
              </span>
            )}
          </span>

          <span className="mt-1 block text-sm text-slate-500 dark:text-slate-400">
            Années / âge :{" "}
            <strong className="font-semibold text-slate-700 dark:text-slate-200">
              {group?.annees_naissance || "non renseigné"}
            </strong>
          </span>
        </span>

        <FaChevronRight
          className={cn(
            "mt-1 shrink-0 text-sm transition",
            selected
              ? "text-blue-600 dark:text-blue-300"
              : "text-slate-300 group-hover:text-blue-500"
          )}
        />
      </div>
    </button>
  );
}

const RenewalModal = ({ isOpen, onClose, adherent, nextSeason }) => {
  const { user } = useUser();

  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const searchRef = useRef(null);

  const birthYear = useMemo(() => getBirthYear(adherent), [adherent]);

  const ageForSeason = useMemo(
    () => getAgeForSeason(birthYear, nextSeason),
    [birthYear, nextSeason]
  );

  const currentGroups = useMemo(
    () => getGroupsFromAdherent(adherent),
    [adherent]
  );

  const currentGroupIds = useMemo(() => {
    return new Set(currentGroups.map((g) => Number(g.id)).filter(Boolean));
  }, [currentGroups]);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedGroupIds([]);
    setFilter("");

    const loadGroups = async () => {
      try {
        setLoadingGroups(true);

        const activeGroups = await getActiveGroupsList();

        setGroups(Array.isArray(activeGroups) ? activeGroups : []);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des groupes actifs:",
          error
        );
        toast.error("Impossible de charger les groupes actifs.");
      } finally {
        setLoadingGroups(false);
      }
    };

    loadGroups();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 80);
    }
  }, [isOpen, loadingGroups]);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape" && !isSaving) onClose(false);
    };

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, isSaving, onClose]);

  const visibleGroups = useMemo(() => {
    const q = normalize(filter);

    if (!q) return groups;

    return groups.filter((g) => getGroupSearchText(g).includes(q));
  }, [groups, filter]);

  const recommendedGroups = useMemo(() => {
    if (!birthYear) return [];

    return visibleGroups.filter((g) =>
      groupIsRecommended(g, birthYear, nextSeason)
    );
  }, [visibleGroups, birthYear, nextSeason]);

  const otherGroups = useMemo(() => {
    if (!birthYear) return visibleGroups;

    return visibleGroups.filter(
      (g) => !groupIsRecommended(g, birthYear, nextSeason)
    );
  }, [visibleGroups, birthYear, nextSeason]);

  const selectedGroups = useMemo(() => {
    const ids = new Set(selectedGroupIds.map(Number));

    return groups.filter((g) => ids.has(Number(g.id)));
  }, [groups, selectedGroupIds]);

  const selectedBirthYears = useMemo(() => {
    return selectedGroups
      .map((g) => g?.annees_naissance)
      .filter(Boolean)
      .join(" / ");
  }, [selectedGroups]);

  const toggleGroup = (groupId) => {
    const id = Number(groupId);

    setSelectedGroupIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((gid) => gid !== id);
      }

      return [...prev, id];
    });
  };

  const notifyAdminRenewal = async () => {
    const token = getAuthToken(user);

    if (!token) {
      console.warn(
        "[RenewalModal] Notification admin non envoyée : token introuvable."
      );
      return;
    }

    const connectedUserName = getConnectedUserLabel(user);

    const adherentName = `${adherent?.prenom || ""} ${
      adherent?.nom || ""
    }`.trim();

    const groupNames =
      selectedGroups
        .map((group) => group?.nom)
        .filter(Boolean)
        .join(" / ") || "groupe non renseigné";

    const notificationAdmin = `${connectedUserName} a effectué le renouvellement de ${
      adherentName || "un adhérent"
    } pour la saison ${nextSeason} sur le(s) groupe(s) : ${groupNames}.`;

    try {
      await sendNotificationToAdmin(notificationAdmin, token);
    } catch (notificationError) {
      console.error(
        "[RenewalModal] Erreur lors de l'envoi de la notification admin:",
        notificationError
      );
    }
  };

  const getRenewalDossier = async () => {
    try {
      const dossier = await getDossierInscription(adherent.id);

      if (!dossier?.id) {
        throw new Error("Dossier d'inscription introuvable.");
      }

      return dossier;
    } catch (error) {
      console.error(
        "[RenewalModal] Impossible de récupérer le dossier d'inscription:",
        error
      );

      throw error;
    }
  };

  const resetDossierInscriptionForRenewal = async (dossierId) => {
    if (!dossierId) {
      throw new Error("ID du dossier d'inscription introuvable.");
    }

    try {
      await updateDossierInscription(
        dossierId,
        DOSSIER_RENEWAL_RESET_PAYLOAD
      );
    } catch (error) {
      console.error(
        "[RenewalModal] Erreur lors de la réinitialisation du dossier d'inscription:",
        error
      );

      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedGroupIds.length) {
      toast.warn("Veuillez sélectionner au moins un groupe.");
      return;
    }

    if (!adherent?.id) {
      toast.error("Adhérent introuvable.");
      return;
    }

    try {
      setIsSaving(true);

      /**
       * On récupère le dossier AVANT de modifier l'adhérent et les groupes.
       * Comme cela, si aucun dossier n'existe, on bloque le renouvellement
       * avant de faire une modification partielle.
       */
      const dossier = await getRenewalDossier();

      await updateAdherent(adherent.id, {
        saison_active: nextSeason,
        statut_inscription: "Inscrit",
      });

      const response = await addAdherentGroups(adherent.id, selectedGroupIds);

      await resetDossierInscriptionForRenewal(dossier.id);

      await notifyAdminRenewal();

      const mergedGroupsMap = new Map();

      currentGroups.forEach((group) => {
        if (group?.id) mergedGroupsMap.set(Number(group.id), group);
      });

      selectedGroups.forEach((group) => {
        if (group?.id) mergedGroupsMap.set(Number(group.id), group);
      });

      const allGroups = Array.from(mergedGroupsMap.values());

      const payload = {
        adherentId: adherent.id,
        nextSeason,
        statut_inscription: "Inscrit",
        selectedGroupIds,
        selectedGroups,
        allGroups,
        dossierId: dossier.id,
        dossierResetPayload: DOSSIER_RENEWAL_RESET_PAYLOAD,
        apiResponse: response,
      };

      toast.success(
        "Renouvellement enregistré. Le dossier d'inscription a été réinitialisé."
      );

      onClose(true, payload);
    } catch (err) {
      console.error(err);

      if (err?.response?.status === 404) {
        toast.error(
          "Aucun dossier d'inscription n'a été trouvé pour cet adhérent. Le renouvellement n'a pas été finalisé."
        );
      } else {
        toast.error(
          "Erreur lors de l’enregistrement du renouvellement ou de la réinitialisation du dossier."
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-end justify-center bg-slate-950/60 px-3 py-3 backdrop-blur-sm sm:items-center sm:py-6"
      onClick={() => {
        if (!isSaving) onClose(false);
      }}
      aria-modal="true"
      role="dialog"
      aria-labelledby="renewal-title"
    >
      <div
        className="relative z-[100000] flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl ring-1 ring-slate-200/70 dark:bg-slate-950 dark:ring-slate-700/70"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-indigo-700 to-violet-700">
          <div className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-14 -left-10 h-40 w-40 rounded-full bg-cyan-300/20 blur-3xl" />

          <div className="relative px-5 py-5 pr-14 sm:px-7">
            <div className="flex items-center gap-3">
              <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20 sm:flex">
                <FaCalendarAlt />
              </div>

              <div className="min-w-0">
                <h3
                  id="renewal-title"
                  className="text-lg font-bold text-white sm:text-xl"
                >
                  Renouvellement saison {nextSeason}
                </h3>

                <p className="mt-1 text-sm text-white/80">
                  Choisissez un ou plusieurs groupes actifs pour cette nouvelle
                  saison.
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              if (!isSaving) onClose(false);
            }}
            disabled={isSaving}
            className="absolute right-3 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Fermer"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
            <div className="mb-5 grid grid-cols-1 gap-3 lg:grid-cols-[1fr_240px]">
              <div className="flex items-center gap-3 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-slate-900">
                <img
                  src={
                    toAbsolute(adherent?.photo_url) ||
                    (adherent?.genre === "Féminin"
                      ? "/img/judokate_avatar.png"
                      : "/img/judoka_avatar.png")
                  }
                  alt={`${adherent?.nom || ""} ${adherent?.prenom || ""}`}
                  className="h-14 w-14 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                />

                <div className="min-w-0 flex-1">
                  <div className="truncate font-bold text-slate-900 dark:text-slate-100">
                    {(adherent?.nom || "").toUpperCase()}{" "}
                    {adherent?.prenom || ""}
                  </div>

                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <span className="inline-flex items-center gap-1">
                      <FaUser />
                      {birthYear || "Année inconnue"}
                    </span>

                    {ageForSeason !== null && (
                      <span>
                        Âge saison {nextSeason} : {ageForSeason} ans
                      </span>
                    )}

                    <span>
                      Saison actuelle : {adherent?.saison_active || "—"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-blue-100 bg-blue-50 p-4 dark:border-blue-500/20 dark:bg-blue-500/10">
                <p className="text-xs font-bold uppercase tracking-wide text-blue-700 dark:text-blue-200">
                  Sélection
                </p>

                <p className="mt-1 text-2xl font-bold text-blue-950 dark:text-white">
                  {selectedGroupIds.length}
                </p>

                <p className="text-sm text-blue-800 dark:text-blue-100">
                  groupe{selectedGroupIds.length > 1 ? "s" : ""} choisi
                  {selectedGroupIds.length > 1 ? "s" : ""}
                </p>
              </div>
            </div>

            {/* <div className="mb-5 rounded-[1.5rem] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100">
              <p className="font-bold">Réinitialisation du dossier</p>
              <p className="mt-1">
                Le certificat médical, sa date de validité, sa validation et le
                lien Drive seront conservés. La licence, les formulaires de
                licence et la validation du règlement seront remis à zéro pour
                la nouvelle saison.
              </p>
            </div> */}

            <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_220px]">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-800 dark:text-slate-100">
                  Rechercher un groupe
                </label>

                <div className="relative">
                  <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input
                    ref={searchRef}
                    type="text"
                    placeholder="Filtrer par nom, année ou âge…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-10 pr-3 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-800 dark:text-slate-100">
                  Nouvelle saison
                </label>

                <input
                  type="text"
                  value={nextSeason || ""}
                  readOnly
                  className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-3 font-bold text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                />
              </div>
            </div>

            {loadingGroups ? (
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-400">
                Chargement des groupes actifs…
              </div>
            ) : (
              <div className="space-y-5">
                {recommendedGroups.length > 0 && (
                  <section>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <h4 className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">
                          <FaUsers />
                        </span>
                        Groupes conseillés
                      </h4>

                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">
                        {recommendedGroups.length} groupe
                        {recommendedGroups.length > 1 ? "s" : ""}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-2">
                      {recommendedGroups.map((group) => (
                        <GroupChoiceCard
                          key={group.id}
                          group={group}
                          selected={selectedGroupIds.includes(
                            Number(group.id)
                          )}
                          recommended
                          alreadyLinked={currentGroupIds.has(
                            Number(group.id)
                          )}
                          onToggle={toggleGroup}
                        />
                      ))}
                    </div>
                  </section>
                )}

                <section>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                      {recommendedGroups.length > 0
                        ? "Autres groupes actifs"
                        : "Groupes actifs"}
                    </h4>

                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">
                      {otherGroups.length} groupe
                      {otherGroups.length > 1 ? "s" : ""}
                    </span>
                  </div>

                  {otherGroups.length ? (
                    <div className="grid grid-cols-1 gap-2">
                      {otherGroups.map((group) => (
                        <GroupChoiceCard
                          key={group.id}
                          group={group}
                          selected={selectedGroupIds.includes(
                            Number(group.id)
                          )}
                          recommended={false}
                          alreadyLinked={currentGroupIds.has(
                            Number(group.id)
                          )}
                          onToggle={toggleGroup}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-400">
                      Aucun autre groupe actif trouvé.
                    </div>
                  )}
                </section>

                {selectedGroupIds.length > 0 && (
                  <div className="rounded-[1.5rem] border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-500/20 dark:bg-blue-500/10">
                    <p className="text-sm text-blue-950 dark:text-blue-100">
                      Groupe{selectedGroupIds.length > 1 ? "s" : ""}{" "}
                      sélectionné{selectedGroupIds.length > 1 ? "s" : ""} :{" "}
                      <span className="font-bold">
                        {selectedGroups.map((g) => g.nom).join(", ")}
                      </span>
                    </p>

                    {selectedBirthYears && (
                      <p className="mt-1 text-xs text-blue-800 dark:text-blue-200">
                        Années / âge : {selectedBirthYears}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 bg-white px-4 py-4 dark:border-white/10 dark:bg-slate-950 sm:flex-row sm:justify-end sm:px-6">
            <Button
              text={
                isSaving ? "Enregistrement…" : "Enregistrer le renouvellement"
              }
              icon={FaSave}
              type="submit"
              disabled={isSaving || !selectedGroupIds.length}
              className="!rounded-xl gap-2 disabled:opacity-60"
            />

            <Button
              text="Annuler"
              icon={FaTimes}
              type="button"
              onClick={() => onClose(false)}
              disabled={isSaving}
              className="!rounded-xl gap-2 disabled:opacity-60"
            />
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default RenewalModal;